import { defineStore } from 'pinia';
import apiClient from '@/axios';

export const useProductOptionsStore = defineStore('productOptions', {
  state: () => ({
    temperatureOptions: [],
    sizeOptions: [],
    categoryOptions: [],
    baseCategoryOptions: [],
    availabilityOptions: [],
    stationOptions: [],
    isLoading: false,
    error: null,
    _lastFetchHash: null,
    _fetchCache: null,
  }),

  getters: {
    getTemperatureOptions: (state) => state.temperatureOptions,
    getSizeOptions: (state) => state.sizeOptions,
    getCategoryOptions: (state) => state.categoryOptions,
    getBaseCategoryOptions: (state) => state.baseCategoryOptions,
    getAvailabilityOptions: (state) => state.availabilityOptions,
    getStationOptions: (state) => state.stationOptions,
  },

  actions: {
    _getCacheKey() {
      return 'all-options';
    },

    async fetchOptions(endpoint) {
      try {
        const response = await apiClient.get(endpoint);
        return response.data;
      } catch (error) {
        this.error = error;
        console.error(`Failed to fetch options from ${endpoint}:`, error);
        throw error;
      }
    },

    async fetchAllOptions(forceRefresh = false) {
      const cacheKey = this._getCacheKey();

      if (!forceRefresh && this._lastFetchHash === cacheKey && this._fetchCache) {
        this.temperatureOptions = this._fetchCache.temperatureOptions || [];
        this.sizeOptions = this._fetchCache.sizeOptions || [];
        this.categoryOptions = this._fetchCache.categoryOptions || [];
        this.baseCategoryOptions = this._fetchCache.baseCategoryOptions || [];
        this.availabilityOptions = this._fetchCache.availabilityOptions || [];
        this.stationOptions = this._fetchCache.stationOptions || [];
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const [temps, sizes, categories, base_categories, availabilities, stations] = await Promise.all([
          this.fetchOptions('v1/admin/product-temperature-option'),
          this.fetchOptions('v1/admin/product-size-option'),
          this.fetchOptions('v1/admin/product-category-option'),
          this.fetchOptions('v1/admin/product-base-category'),
          this.fetchOptions('v1/admin/product-availability-option'),
          this.fetchOptions('v1/admin/product-station-option'),
        ]);

        const validTemps = Array.isArray(temps) ? temps : [];
        const validSizes = Array.isArray(sizes) ? sizes : [];
        const validCategories = Array.isArray(categories) ? categories : [];
        const validBaseCategories = Array.isArray(base_categories?.data) ? base_categories.data : [];
        const validAvailabilities = Array.isArray(availabilities) ? availabilities : [];
        const validStations = Array.isArray(stations) ? stations : [];

        this.temperatureOptions = validTemps;
        this.sizeOptions = validSizes;
        this.categoryOptions = validCategories;
        this.baseCategoryOptions = validBaseCategories;
        this.availabilityOptions = validAvailabilities;
        this.stationOptions = validStations;

        this._lastFetchHash = cacheKey;
        this._fetchCache = {
          temperatureOptions: validTemps,
          sizeOptions: validSizes,
          categoryOptions: validCategories,
          baseCategoryOptions: validBaseCategories,
          availabilityOptions: validAvailabilities,
          stationOptions: validStations,
        };

      } catch (error) {
        this.error = error.message || 'Failed to fetch options';
        this._lastFetchHash = null;
        this._fetchCache = null;
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    clearOptionsCache() {
      this._lastFetchHash = null;
      this._fetchCache = null;
    },

    async fetchSpecificOptions(optionsToFetch = []) {
      const cacheKey = `specific-${optionsToFetch.sort().join(',')}`;

      if (this._lastFetchHash === cacheKey && this._fetchCache) {
        if (this._fetchCache.temperatureOptions) this.temperatureOptions = this._fetchCache.temperatureOptions;
        if (this._fetchCache.sizeOptions) this.sizeOptions = this._fetchCache.sizeOptions;
        if (this._fetchCache.categoryOptions) this.categoryOptions = this._fetchCache.categoryOptions;
        if (this._fetchCache.baseCategoryOptions) this.baseCategoryOptions = this._fetchCache.baseCategoryOptions;
        if (this._fetchCache.availabilityOptions) this.availabilityOptions = this._fetchCache.availabilityOptions;
        if (this._fetchCache.stationOptions) this.stationOptions = this._fetchCache.stationOptions;
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const promises = [];
        const optionMap = {};

        if (optionsToFetch.includes('temperature') || optionsToFetch.length === 0) {
          promises.push(this.fetchOptions('v1/admin/product-temperature-option').then(data => optionMap.temperature = data));
        }
        if (optionsToFetch.includes('size') || optionsToFetch.length === 0) {
          promises.push(this.fetchOptions('v1/admin/product-size-option').then(data => optionMap.size = data));
        }
        if (optionsToFetch.includes('category') || optionsToFetch.length === 0) {
          promises.push(this.fetchOptions('v1/admin/product-category-option').then(data => optionMap.category = data));
        }
        if (optionsToFetch.includes('base_category') || optionsToFetch.length === 0) {
          promises.push(this.fetchOptions('v1/admin/product-base-category').then(data => optionMap.base_category = data));
        }
        if (optionsToFetch.includes('availability') || optionsToFetch.length === 0) {
          promises.push(this.fetchOptions('v1/admin/product-availability-option').then(data => optionMap.availability = data));
        }
        if (optionsToFetch.includes('station') || optionsToFetch.length === 0) {
          promises.push(this.fetchOptions('v1/admin/product-station-option').then(data => optionMap.station = data));
        }

        await Promise.all(promises);

        const validTemps = optionMap.temperature || [];
        const validSizes = optionMap.size || [];
        const validCategories = optionMap.category || [];
        const validBaseCategories = optionMap.base_category || [];
        const validAvailabilities = optionMap.availability || [];
        const validStations = optionMap.station || [];

        if (optionMap.temperature) this.temperatureOptions = validTemps;
        if (optionMap.size) this.sizeOptions = validSizes;
        if (optionMap.category) this.categoryOptions = validCategories;
        if (optionMap.base_category) this.baseCategoryOptions = validBaseCategories;
        if (optionMap.availability) this.availabilityOptions = validAvailabilities;
        if (optionMap.station) this.stationOptions = validStations;

        this._lastFetchHash = cacheKey;
        this._fetchCache = {
          temperatureOptions: validTemps,
          sizeOptions: validSizes,
          categoryOptions: validCategories,
          baseCategoryOptions: validBaseCategories,
          availabilityOptions: validAvailabilities,
          stationOptions: validStations,
        };

      } catch (error) {
        this.error = error.message || 'Failed to fetch specific options';
        this._lastFetchHash = null;
        this._fetchCache = null;
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});